import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Star } from 'lucide-react';
import '../styles/ourwork.css';

const AnimatedCharacter = ({ char, index, baseDelay, isOrange = false, customStyles = {} }) => {
  return (
    <motion.span
      key={index}
      initial={{ 
        opacity: 0, 
        x: -50,
        rotate: -10
      }}
      whileInView={{ 
        opacity: 1, 
        x: 0,
        rotate: 0
      }}
      transition={{
        delay: baseDelay + index * 0.05,
        type: "spring",
        stiffness: 100,
        damping: 10
      }}
      viewport={{ once: false }}
      style={{
        display: 'inline-block',
        willChange: 'transform, opacity',
        margin: char === ' ' ? '0 0.25em' : '0', 
        color: isOrange ? '#FF6B35' : 'inherit',
        ...customStyles
      }}
      className={`char-${index + 1}`}
    >
      {char}
    </motion.span>
  );
};

const ClientReviewsHeading = () => {
  const clientText = "CLIENT";
  const reviewsText = "REVIEWS";

  return (
    <motion.div
      className="about-header"
      initial={{ opacity: 0, y: -50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, type: "spring" }}
      viewport={{ once: false }}
    >
      <div className="about-heading">
        <div className="heading-wrapper">
          {clientText.split("").map((char, index) => (
            <AnimatedCharacter
              key={`client-${index}`}
              char={char}
              index={index}
              baseDelay={0.2}
              customStyles={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(2.5rem, 5vw, 5rem)',
                color: '#ffffff'
              }}
            />
          ))}
        </div>
        <div className="heading-wrapper">
          {reviewsText.split("").map((char, index) => (
            <AnimatedCharacter
              key={`reviews-${index}`}
              char={char}
              index={index}
              baseDelay={0.4}
              isOrange={char !== " "}
              customStyles={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(2.5rem, 5vw, 5rem)'
              }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const ClientReviews = () => {
  const [activeReview, setActiveReview] = useState(0);

  const reviews = [
    {
      name: "Klaus Schneider",
      country: "Germany",
      role: "CEO, TechNova GmbH",
      service: "E-Commerce Website Development",
      text: "Nexolve transformed our digital presence with an incredibly intuitive and robust e-commerce platform. Their attention to detail and deep understanding of German market nuances was truly impressive. The website not only looks stunning but has increased our online sales by 47% in just three months.",
      rating: 5
    },
    {
      name: "Emily Hartley",
      country: "United Kingdom", 
      role: "CTO, FinTech Solutions Ltd",
      service: "AI Development",
      text: "Working with Nexolve was a game-changer for our AI integration project. Their team demonstrated exceptional technical prowess and innovative thinking. They developed a machine learning model that streamlined our customer service processes, reducing response times by 60% and significantly improving customer satisfaction.",
      rating: 4.5
    },
    {
      name: "Michael Thompson",
      country: "United States",
      role: "Founder, Phoenix Startup Accelerator",
      service: "Mobile App Development",
      text: "Nexolve's mobile app development team is nothing short of exceptional. They took our complex business concept and transformed it into a sleek, user-friendly mobile application. Their agile methodology and constant communication made the entire process seamless and enjoyable.",
      rating: 5
    },
    {
      name: "Sophie Laurent",
      country: "France",
      role: "Marketing Director, Global Retail",
      service: "DevOps Consulting",
      text: "Nexolve's DevOps solutions revolutionized our infrastructure. They implemented cutting-edge containerization and continuous integration strategies that dramatically improved our deployment speed and system reliability. Their expertise is truly world-class.",
      rating: 4.5
    },
    {
      name: "Akira Tanaka",
      country: "Japan",
      role: "Innovation Lead, Tokyo Tech",
      service: "Web Development",
      text: "The web platform Nexolve created for us is a perfect blend of aesthetic design and functional excellence. They understood our complex requirements and delivered a solution that exceeded our expectations in both performance and user experience.",
      rating: 5
    },
    {
      name: "Ricardo Martinez",
      country: "Brazil",
      role: "Operations Manager, LatAm Innovations",
      service: "AI Development",
      text: "Nexolve's AI solutions have been transformative for our business intelligence processes. Their team developed a predictive analytics model that provides insights we never thought possible, giving us a significant competitive advantage.",
      rating: 4.5
    },
    {
      name: "Amara Okonkwo",
      country: "Nigeria",
      role: "Startup Founder, Digital Africa",
      service: "Full-Stack Development",
      text: "From concept to execution, Nexolve demonstrated unparalleled technical expertise and commitment. They didn't just build a website; they created a digital ecosystem that truly represents our brand's vision and potential.",
      rating: 5
    }
  ];

  // Auto-slide reviews every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveReview((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        fill={i < Math.floor(rating) ? "#FFA07A" : "none"} 
        color="#FFA07A" 
        strokeWidth={2}
      />
    ));
  };

  return (
    <motion.div 
      className="client-reviews-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <ClientReviewsHeading />

      <div className="reviews-slider">
        <AnimatePresence mode="wait">
          <motion.div 
            key={activeReview}
            className="review-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30 
            }}
          >
            <Quote className="quote-icon" />
            <motion.p 
              className="review-text"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              "{reviews[activeReview].text}"
            </motion.p>
            
            <div className="review-details">
              <motion.div 
                className="review-stars"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {renderStars(reviews[activeReview].rating)}
              </motion.div>
              <div className="reviewer-info">
                <motion.h3
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  {reviews[activeReview].name}
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  {reviews[activeReview].role} | {reviews[activeReview].country}
                </motion.p>
                <motion.span 
                  className="service-tag"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  {reviews[activeReview].service}
                </motion.span>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="review-navigation">
          {reviews.map((_, index) => (
            <motion.button
              key={index}
              className={`nav-dot ${activeReview === index ? 'active' : ''}`}
              onClick={() => setActiveReview(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default ClientReviews;